import { useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { parseNumber } from "./numberInput";
import { fmtMoney } from "./money";
import { rmdFor, rmdStartAge } from "./retire";

const TOOL_INFO_CONTENT = {
  what: `Calculates your Required Minimum Distribution (RMD): the amount the IRS requires you to withdraw each year from traditional IRAs and most workplace retirement accounts once you reach the starting age for your birth year.`,
  how: `Enter your birth year, the year you want the RMD for, and your account balance on December 31 of the previous year. The RMD is the balance divided by the IRS Uniform Lifetime Table divisor for your age that year.`,
  tip: `The starting age depends on your birth year: 72 if you were born before 1951, 73 if born 1951–1959, 75 if born 1960 or later. Your first RMD can be delayed until April 1 of the following year, but then you take two distributions that year.`,
};
const usd = (n: number) => fmtMoney(n, "USD", 2);

export default function RmdCalculator() {
  const thisYear = new Date().getFullYear();
  const [birth, setBirth] = useState("1951");
  const [year, setYear] = useState(String(thisYear));
  const [balance, setBalance] = useState("500000");

  const by = parseNumber(birth), yr = parseNumber(year), bal = parseNumber(balance);
  const error =
    by == null || by < 1900 || by > thisYear ? "Enter a valid birth year." : yr == null || yr < 2022 || yr > 2100 ? "Enter a year from 2022 to 2100."
    : yr! - by! > 120 ? "That makes the age over 120: check the birth year and RMD year."
    : bal == null || bal < 0 ? "Enter your account balance (0 or more)." : "";
  const age = error ? 0 : yr! - by!;
  const start = error ? 73 : rmdStartAge(by!);
  const before = !error && age < start;
  const { rmd, divisor } = error ? { rmd: 0, divisor: 0 } : rmdFor(bal!, Math.max(age, 72));
  const future = !error && yr! > thisYear + 1;
  const { blocked } = useLogToolUsage(!error && !before);

  return (
    <ToolContainer>
      <div className="max-w-md mx-auto space-y-6">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Birth year</Label><Input inputMode="numeric" aria-label="Birth year" value={birth} onChange={(e) => setBirth(e.target.value)} /></div>
          <div><Label>RMD for the year</Label><Input inputMode="numeric" aria-label="RMD year" aria-invalid={!!error && /year/.test(error)} value={year} onChange={(e) => setYear(e.target.value)} /></div>
        </div>
        <div><Label>Account Balance as of Dec 31 Last Year ($)</Label><Input inputMode="decimal" aria-label="Account balance" value={balance} onChange={(e) => setBalance(e.target.value)} /></div>
        {error && <p className="text-sm text-red-600" data-testid="rmd-error">{error}</p>}
        {future && <p className="text-xs p-3 rounded-lg border bg-card" data-testid="rmd-future">{yr} is a future year. This uses today's IRS Uniform Lifetime Table and the balance you enter (enter your expected balance on Dec 31, {yr! - 1}). The IRS can change the table or the starting ages, so treat this as a rough preview.</p>}
        {!error && (blocked ? (
          <p className="text-sm text-center text-muted-foreground py-4">— blocked at today's usage limit —</p>
        ) : before ? (
          <p className="text-sm text-center p-4 border rounded-lg bg-muted/30" data-testid="rmd-before">
            You turn {age} in {yr}. RMDs start at age {start} for people born in {by} (73 if born 1951–1959; 75 if born 1960 or later), so you have no required distribution yet. If you were {start}, it would be {usd(rmdFor(bal!, start).rmd)}.
          </p>
        ) : (
          <div className="p-4 rounded-xl border bg-primary/5 text-center">
            <p className="text-xs text-muted-foreground">Required Minimum Distribution for {yr} (age {age})</p>
            <p className="text-2xl font-bold text-primary" data-testid="rmd-amount">{usd(rmd)}</p>
            <p className="text-xs text-muted-foreground mt-1">Divisor: {divisor} (IRS Uniform Lifetime Table) · about {usd(rmd / 12)} a month</p>
            {age === start && <p className="text-xs text-muted-foreground mt-2">This is your first RMD year: you may delay it to April 1 of next year, but then you take two that year.</p>}
          </div>
        ))}
        <p className="text-xs text-muted-foreground text-center">
          For account owners, using the Uniform Lifetime Table (the usual case). A different table applies if your sole beneficiary is a spouse more than 10 years younger (Joint Life table), and inherited accounts follow other rules (Single Life table / 10-year rule): see IRS Publication 590-B. Not tax advice.
        </p>
      </div>
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
